import { useState, useEffect } from "react";
import type { Theme, FontPairing } from "@structurify/shared-types";
import { MonoLabel, QuietButton } from "../components/primitives/index.ts";
import { listAuditEntries, type AuditEntry } from "../api/audit.ts";

const FILTER_OPTIONS = ["all", "signin", "uploaded", "extracted"] as const;

interface Props {
  theme: Theme;
  fonts: FontPairing;
  session: string | undefined;
  onBack: () => void;
}

function actionColor(action: string, theme: Theme): string {
  if (action === "document.extracted") return theme.ink;
  if (action === "document.uploaded") return theme.inkSoft;
  return theme.inkMute;
}

function formatTs(iso: string): string {
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function StageAudit({ theme, fonts, session, onBack }: Props) {
  const [filter, setFilter] = useState("all");
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) return;
    setLoading(true);
    listAuditEntries(session)
      .then((data) => { setEntries(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [session]);

  const filtered = filter === "all" ? entries : entries.filter((e) => e.action.includes(filter));

  return (
    <div style={{ flex: 1, overflow: "auto" }}>
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "clamp(20px, 4vw, 48px)" }}>
        <div style={{ opacity: 0, animation: "fadeUp 500ms forwards" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 10 }}>
            <MonoLabel theme={theme} fonts={fonts} size={10}>AUDIT LOG</MonoLabel>
            <div style={{ flex: 1, height: 1, background: theme.rule }} />
            <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkMute}>SOC 2 · IMMUTABLE · 7-YEAR RETENTION</MonoLabel>
          </div>

          <h2 style={{ fontFamily: fonts.sans, fontWeight: 500, fontSize: "clamp(24px, 4vw, 36px)", lineHeight: 1.1, letterSpacing: "-0.025em", color: theme.ink, margin: "14px 0 10px 0" }}>
            Every value, every change, every export.
          </h2>
          <p style={{ fontFamily: fonts.sans, fontSize: 14, color: theme.inkSoft, lineHeight: 1.55, margin: 0, marginBottom: 24, maxWidth: 620 }}>
            Each row is signed and timestamped. Filter by action, or download the full log as CSV.
          </p>
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
          {FILTER_OPTIONS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.06em", padding: "5px 10px", textTransform: "uppercase", background: filter === f ? theme.ink : "transparent", color: filter === f ? theme.docPaper : theme.inkSoft, border: `1px solid ${filter === f ? theme.ink : theme.rule}`, cursor: "pointer" }}>
              {f}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button style={{ fontFamily: fonts.sans, fontSize: 12, color: theme.ink, background: "transparent", border: `1px solid ${theme.rule}`, padding: "5px 12px", cursor: "pointer" }}>
            Download CSV
          </button>
        </div>

        <div style={{ border: `1px solid ${theme.rule}`, background: theme.surface, overflow: "auto" }}>
          {loading ? (
            <div style={{ padding: "32px 20px", fontFamily: fonts.mono, fontSize: 11, color: theme.inkMute, letterSpacing: "0.06em", textAlign: "center" }}>
              LOADING…
            </div>
          ) : !session ? (
            <div style={{ padding: "32px 20px", fontFamily: fonts.sans, fontSize: 14, color: theme.inkMute, textAlign: "center" }}>
              Sign in to view your audit log.
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: "32px 20px", fontFamily: fonts.sans, fontSize: 14, color: theme.inkMute, textAlign: "center" }}>
              No entries yet.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: fonts.sans, fontSize: 13, color: theme.ink, minWidth: 720 }}>
              <thead>
                <tr>
                  {["Timestamp", "Action", "Detail", "Hash"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontFamily: fonts.mono, fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.inkMute, fontWeight: 500, borderBottom: `1px solid ${theme.rule}` }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => (
                  <tr key={e.id} style={{ borderTop: i > 0 ? `1px solid ${theme.rule}` : "none" }}>
                    <td style={{ padding: "10px 14px", fontFamily: fonts.mono, fontSize: 11, color: theme.inkSoft, whiteSpace: "nowrap" }}>{formatTs(e.ts)}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: actionColor(e.action, theme) }}>{e.action}</span>
                    </td>
                    <td style={{ padding: "10px 14px", color: theme.inkSoft, fontSize: 12 }}>{e.detail}</td>
                    <td style={{ padding: "10px 14px", fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute }}>{e.hash}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {filtered.length > 0 && (
          <div style={{ marginTop: 18, padding: "10px 14px", border: `1px dashed ${theme.rule}`, fontFamily: fonts.mono, fontSize: 10.5, color: theme.inkMute, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: theme.inkSoft }}>{filtered.length} ENTRIES</span>
            <div style={{ flex: 1 }} />
            <span>SHA-256 SIGNED</span>
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          <QuietButton theme={theme} fonts={fonts} onClick={onBack}>Back</QuietButton>
        </div>
      </div>
    </div>
  );
}
